// Global Azure Maps Manager
// Handles loading and cleanup of Azure Maps SDK across the entire application

declare global {
    interface Window {
        atlas: any;
        azureMapsManager?: AzureMapsManager;
    }
}

class AzureMapsManager {
    private static instance: AzureMapsManager;
    private isLoaded = false;
    private isLoading = false;
    private loadPromise: Promise<void> | null = null;
    private activeInstances = new Set<string>();

    private constructor() {}

    public static getInstance(): AzureMapsManager {
        if (!AzureMapsManager.instance) {
            AzureMapsManager.instance = new AzureMapsManager();
        }
        return AzureMapsManager.instance;
    }

    public async loadAzureMaps(): Promise<void> {
        // If already loaded, return immediately
        if (this.isLoaded && window.atlas) {
            return Promise.resolve();
        }

        // If currently loading, return the existing promise
        if (this.isLoading && this.loadPromise) {
            return this.loadPromise;
        }

        // Start loading
        this.isLoading = true;
        this.loadPromise = this.loadScripts();

        try {
            await this.loadPromise;
            this.isLoaded = true;
            this.isLoading = false;
        } catch (error) {
            this.isLoading = false;
            this.loadPromise = null;
            throw error;
        }
    }

    private loadScripts(): Promise<void> {
        return new Promise((resolve, reject) => {
            // Check if script already exists
            const existingScript = document.querySelector('script[src*="atlas.min.js"]');
            if (existingScript) {
                // Script exists, wait for it to load
                if (window.atlas) {
                    resolve();
                    return;
                }
                
                existingScript.addEventListener('load', () => resolve());
                existingScript.addEventListener('error', () => reject(new Error('Failed to load Azure Maps SDK')));
                return;
            }

            // Create and load script
            const script = document.createElement('script');
            script.src = 'https://atlas.microsoft.com/sdk/javascript/mapcontrol/2/atlas.min.js';
            
            script.onload = () => {
                // Add CSS if not already present
                const existingCSS = document.querySelector('link[href*="atlas.min.css"]');
                if (!existingCSS) {
                    const link = document.createElement('link');
                    link.rel = 'stylesheet';
                    link.href = 'https://atlas.microsoft.com/sdk/javascript/mapcontrol/2/atlas.min.css';
                    document.head.appendChild(link);
                }
                resolve();
            };

            script.onerror = () => {
                reject(new Error('Failed to load Azure Maps SDK'));
            };

            document.head.appendChild(script);
        });
    }

    public registerInstance(instanceId: string): void {
        this.activeInstances.add(instanceId);
        console.log(`Azure Maps instance registered: ${instanceId}. Active instances: ${this.activeInstances.size}`);
    }

    public unregisterInstance(instanceId: string): void {
        this.activeInstances.delete(instanceId);
        console.log(`Azure Maps instance unregistered: ${instanceId}. Active instances: ${this.activeInstances.size}`);
        
        // If no more instances are active, we could potentially clean up
        // But we'll keep the SDK loaded for better performance
    }

    public isInstanceActive(instanceId: string): boolean {
        return this.activeInstances.has(instanceId);
    }

    public getActiveInstanceCount(): number {
        return this.activeInstances.size;
    }

    public isSDKLoaded(): boolean {
        return this.isLoaded && !!window.atlas;
    }
}

// Initialize global instance
if (typeof window !== 'undefined') {
    window.azureMapsManager = AzureMapsManager.getInstance();
}

export const azureMapsManager = AzureMapsManager.getInstance();
